import {
  Component,
  OnInit,
  ViewChildren,
  QueryList,
  ElementRef,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { concatMap, switchMap, take, distinctUntilChanged } from 'rxjs/operators';
import { Observable, Subscription, firstValueFrom, forkJoin } from 'rxjs';
import { DatePipe } from '@angular/common';
import { ProductionCostService } from './production-cost.service';

import {
  CostItem,
  CostItemService,
  EnumService,
  ItemGroup,
  ItemGroupService,
  ModalService,
  Position,
  Project,
  ProjectManagerService,
  CostItemComment,
  ItemGroupComment,
  OpsVersion,
  PaidByListItem,
  AccordionCategory,
  LocalStorageService,
  DataSyncService,
  CompanyService,
  NameFilter,
  ItemGroupCommentService,
  ProjectStatusGuardService,
} from '@ops/shared-types';
import { Miscellanious } from '@ops/global-config';
import { NameIdPair } from './cost-accordion-item/cost-accordion-item.component';
import { TranslateService } from '@ngx-translate/core';

export interface DisplayItem extends CostItem {
  estimatedTotal: number;
  estimatedCurrencyCode?: string;
  estimatedExchangeRate?: number;
  isExpanded?: boolean;
}

export interface MatchedCostItem {
  estimatedTotal: number;
  currencyCode: string;
  exchangeRate: number;
}

export interface LoadedItem extends CostItem {
  matchedCostItem: MatchedCostItem;
}

export interface CostAccordionCategory {
  _id?: string;
  project: string;
  department: string;
  name?: string;
  items: Array<DisplayItem>;
  estimate: number;
  actual: number;
  comments?: Array<ItemGroupComment>;
  hasComment?: boolean;
  open?: boolean;
  positions?: Array<Position>;
  orderNumber?: number;
  /**
   * Disabled Collapse during search through items
   */
  disabled?: boolean;

  isLoaded?: boolean;
}
//TODO: change name to ProjectCostComponent
@Component({
  selector: 'app-production-cost',
  templateUrl: './production-cost.component.html',
  styleUrls: ['./production-cost.component.scss'],
  providers: [DatePipe],
})
export class ProductionCostComponent implements OnInit, OnDestroy {
  @ViewChildren('content')
  contentElements: QueryList<ElementRef>;

  projectId: string;

  projectCost: Project = { _id: '', projectDepartments: [] };
  approvedEstimates: Array<Project>;
  approvedVersions: Array<OpsVersion>;
  estimateId: string;
  estimateItems: Array<CostItem>;
  // estimateGroups: Array<ItemGroup> = [];
  accordionList: Array<CostAccordionCategory> = [];
  difference = 0;
  addDepartment: boolean;

  allItemsToDisplay: Array<DisplayItem> = [];

  departments: Array<{ name: string; isTeam: boolean; isSelected: boolean }>;
  allPositions: Array<Position>;
  sortedPositions: Array<{ department: string; positions: Array<Position> }> =
    [];

  addItemGroup: { department: string; name?: string };
  displayedPositions: Array<Position> = [];
  searchPosition: string;
  positionsToAdd: Array<string> = [];
  searchResults: Array<string>;

  inHouseExpense = 0;
  projectExpense = 0;
  isLoading = true;
  //workaround TODO refactor;
  noEstimateApproved: boolean;

  renameDepartmentId: string;

  feeTemp: number;
  taxTemp: number;
  discountPercentageTemp: number;
  discountSpecificTemp: number;

  focusedItem: CostItem = {};
  focusedDepartmentId: string;

  sub0: Subscription;
  sub1: Subscription;
  sub2: Subscription;
  sub3: Subscription;

  allPaidByOptionsFlat: Array<NameIdPair>;
  companyCurrencyCode?: string;
  projectCurrencyCode?: string;

  assignToValues: Array<NameIdPair>;

  isCostMapped: boolean = false;
  isEstimateMapped: boolean = false;

  refreshTrigger: boolean = false;
  isRefreshing: boolean = false;

  itemIdToDisplay: string;
  sub21: Subscription;

  mainTimer: number = 0;

  profitability: number = 0;

  constructor(
    private router: ActivatedRoute,
    private pm: ProjectManagerService,
    private groupDataService: ItemGroupService,
    private itemDataService: CostItemService,
    private enumService: EnumService,
    private modalService: ModalService,
    private productionCostService: ProductionCostService,
    private datePipe: DatePipe,
    private localStorageService: LocalStorageService,
    private changeDetector: ChangeDetectorRef,
    private dataSyncService: DataSyncService,
    private translateService: TranslateService,
    private companyService: CompanyService,
    private itemGroupCommentService: ItemGroupCommentService,
    private projectStatusGuard: ProjectStatusGuardService
  ) {
    this.companyCurrencyCode =
      this.localStorageService.getCurrentCompany()?.baseCurrencyCode;
  }

  get haveData(): boolean {
    return this.projectCost?._id && this.projectCost._id !== '';
  }

  // ✅ FIXED: Get current company ID from localStorage instead of project data
  getCurrentCompanyId(): string {
    return this.localStorageService.getCurrentCompany()?._id || '';
  }

  resetValues() {
    // this.pid = this.pm.getCurrentProject()?._id;

    this.projectCost = { _id: '', projectDepartments: [] };

    this.approvedEstimates = [];
    this.approvedVersions = [];
    this.estimateId = '';
    this.estimateItems = [];

    this.accordionList = [];
    this.difference = 0;
    this.addDepartment = false;
    this.addItemGroup = undefined;

    this.allItemsToDisplay = [];

    this.departments = [];
    this.allPositions = [];
    this.sortedPositions = [];

    this.displayedPositions = [];
    this.searchPosition = '';
    this.searchResults = [];

    this.inHouseExpense = 0;
    this.projectExpense = 0;

    //workaround TODO refactor;

    this.feeTemp = 0;
    this.taxTemp = 0;
    this.discountPercentageTemp = 0;
    this.discountSpecificTemp = 0;

    this.focusedItem = {};
  }

  async ngOnInit() {
    this.mainTimer = performance.now();
    this.productionCostService.refreshPaidByOptions();
    // Clean up any existing subscriptions before creating new ones
    this.cleanupSubscriptions();

    // Use distinctUntilChanged to prevent multiple triggers for same params
    this.sub0 = this.router.paramMap.pipe(
      distinctUntilChanged((prev, curr) => {
        return prev.get('projectId') === curr.get('projectId') && 
               prev.get('itemId') === curr.get('itemId');
      })
    ).subscribe((params) => {
      this.projectId = params.get('projectId');
      this.itemIdToDisplay = params.get('itemId') ?? '';
      this.isLoading = true;
      this.resetValues();

      this.onParamChangeTriggered();
    });

    //Subscribe to company changes to trigger change detection
    //TODO: do we need this?
    this.companyService.selectedCompany$.subscribe(company => {
      if (company?._id) {
        this.changeDetector.markForCheck();
      }
    });

    this.dataSyncService.refetchVersionListener$.next(new Date().getTime());

    this.assignToValues = this.productionCostService.assignToValues;

  }

  setPaidByOptions() {
    this.allPaidByOptionsFlat = [];

    this.productionCostService.paidByOptions.forEach((o) => {
      this.allPaidByOptionsFlat.push(...o.pairList);
    });
  }

  async getAccordionCategories(){
    const itemGroups = await  firstValueFrom(this.groupDataService.getAllProjectItemGroups(this.projectCost._id));
    return itemGroups.map((ig) => {
      return {
        ...ig,
        isLoaded: false,
      };
    });
  }

  // Centralized subscription cleanup method
  private cleanupSubscriptions() {
    if (this.sub0?.unsubscribe) {
      this.sub0.unsubscribe();
    }
    if (this.sub1?.unsubscribe) {
      this.sub1.unsubscribe();
    }
    if (this.sub2?.unsubscribe) {
      this.sub2.unsubscribe();
    }
    if (this.sub3?.unsubscribe) {
      this.sub3.unsubscribe();
    }
  }

  async onParamChangeTriggered() {
    // Clean up existing subscriptions before creating new ones
    this.cleanupSubscriptions();

    this.projectId = this.projectId
      || (await firstValueFrom(this.router.paramMap)).get('projectId')
      || this.pm.getCurrentProject()?._id ;

    const companyId = this.getCurrentCompanyId();
    if (!this.projectId || !companyId) {
      this.isLoading = false;
      this.changeDetector.markForCheck();
      return;
    }

    //this can go to service
    this.sub1 = this.enumService.getAllDepartments(this.projectId)
      .pipe(switchMap((depts: Array<{ name: string; isTeam: boolean }>) => {
        this.departments = depts.map((d) => {
          return { name: d.name, isTeam: d.isTeam, isSelected: false };
        });
        return this.groupDataService.getItemGroupPositions(this.projectId, 'all');
      }),
    ).subscribe((positions: Array<any>) => {
      this.allPositions = positions.map((p) => {
        return new Position(p.name, p.abbreviation, p.department, false);
      });
      this.departments?.forEach((c) =>
        this.sortedPositions.push({
          department: c.name,
          positions: this.allPositions
            .filter((p) => p.department === c.name)
            .sort((a, b) => a.name.localeCompare(b.name)),
        })
      );
    });

    // const versions = (await firstValueFrom(this.pm.getProjectVersioningInfo(this.projectId))).versions;

    // this.approvedVersions = versions.filter((v) => v.isApproved || v.isAddedToApproved);
    // this.estimateId =
    //       this.approvedVersions.find((v) => v.isApproved)?._id ?? versions[0]._id;
    // this.noEstimateApproved = this.approvedVersions.length === 0;

    // const observables: Array<Observable<Array<CostItem>>> = this.approvedVersions.map((v: any) => this.itemDataService.getCostItemsByProject(v._id));
    // if (observables.length) {
    //   this.sub3 = forkJoin(observables).subscribe((items: Array<Array<CostItem>>) => {
    //     this.estimateItems = [];
    //     if (!this.noEstimateApproved) {
    //           items.forEach((i) => {
    //             i.forEach((ei) => {
    //               this.estimateItems.push(ei);
    //             });
    //           });
    //         };
    //     this.productionCostService.setEstimateItems(this.estimateItems);
    //     this.isEstimateMapped = true;
    //     if(this.isCostMapped) {
    //       this.mapEstimationToCost();
    //     }
    //   });
    // }

    this.sub3 = this.pm.getProjectVersioningInfo(this.projectId).subscribe((versioningInfo: {versions: Array<OpsVersion>}) => {
      this.approvedVersions = versioningInfo.versions.filter((v) => v.isApproved || v.isAddedToApproved);
      this.projectCost.estimatedCost = this.approvedVersions.reduce((res, v) => res + v.total, 0);
      this.calculateProfitability();
      
      this.changeDetector.markForCheck();
    });

    try {
      this.projectCost._id = await firstValueFrom(
        this.pm.getOrCreateProjectCost(this.projectId, companyId)
      );
    } catch (err) {
      console.error('Failed to load project cost (POST project/module):', err);
      this.isLoading = false;
      this.changeDetector.markForCheck();
      return;
    }

    this.pm.getProjectById(this.projectCost._id).subscribe((project: Project) => {
      this.projectCost = {...this.projectCost, ...project};
      this.pm.setCurrentProject(this.projectCost);
      // Update project status guard service with the latest project data (including status)
      this.projectStatusGuard.setCurrentProject(this.projectCost);
      this.changeDetector.markForCheck();
    });

    const itemGroups$ = this.groupDataService.getAllProjectItemGroups(this.projectCost._id);
    const items$ = this.itemDataService.getCostItemsLoadedWithEstimateItemsValues(this.projectCost._id);

    this.sub2 = forkJoin([itemGroups$, items$]).subscribe(([groups, items] : [Array<ItemGroup>, Array<LoadedItem>]) => {
      this.projectCost.projectDepartments = groups.map((g) => {
        
        g.positions = this.sortedPositions.find(
          (p) => p.department === g.department
        ).positions;

        const groupItems = items.filter((i) => {
          if(this.itemIdToDisplay && i._id === this.itemIdToDisplay) {
            this.focusedDepartmentId = i.itemGroup;
          }
          return i.itemGroup === g._id
        });

        //map items to projectDepartments - do we need this?
        g.items = groupItems.map<CostItem>((i: LoadedItem) => {
          return {
            ...i
          };
        });
        const groupedItemsToDisplay = groupItems.map<DisplayItem>((i: LoadedItem, index: number) => {
          return {
            ...i,
            isExpanded: this.itemIdToDisplay === i._id,
            orderNumber: index,
            estimatedCurrencyCode: i.matchedCostItem?.currencyCode ?? "",
            estimatedTotal:  i.matchedCostItem?.estimatedTotal ?? 0,
            estimatedExchangeRate: i.matchedCostItem?.exchangeRate ?? 1,
          };
        }).sort(
          (a, b) => a.orderNumber - b.orderNumber
        );

        this.allItemsToDisplay.push(...groupedItemsToDisplay);

        this.accordionList.push({
          _id: g._id, 
          project: g.project,
          department: g.department,
          name: g.name,
          items: groupedItemsToDisplay,
          comments: g.comments,
          hasComment: g.hasComment,
          orderNumber: g.orderNumber,
          disabled: false,
          open: this.focusedDepartmentId === g._id,
          estimate: groupedItemsToDisplay.reduce((res, i) => res + i.estimatedTotal * i.estimatedExchangeRate, 0),
          positions: g.positions,
          actual:
            g.items.reduce(
              (res, i) =>
                res +
                (i.currencyCode !== this.companyCurrencyCode
                  ? i.exchangeRate * i.total
                  : i.total),
              0
            ) ?? 0,
        });

        return g;
      });

      this.projectCost.actualCost = this.accordionList.reduce((res, i) => res + i.actual, 0);
  
      this.inHouseExpense = items
        .filter((i) => i.costType?.toUpperCase() === 'IN-HOUSE')
        .reduce(
          (res, i) =>
            res +
            (i.currencyCode !== this.companyCurrencyCode
              ? i.exchangeRate * i.total
              : i.total),
          0
        );
      this.inHouseExpense = Number(this.inHouseExpense.toFixed(2));

      this.projectExpense = items
        .filter((i) => i.costType?.toUpperCase() != 'IN-HOUSE')
        .reduce(
          (res, i) =>
            res +
            (i.currencyCode !== this.companyCurrencyCode
              ? i.exchangeRate * i.total
              : i.total),
          0
        );
      this.projectExpense = Number(this.projectExpense.toFixed(2));

      this.calculateProfitability();

      this.isCostMapped = true;
      this.isLoading = false;

      //Auto scroll to the item that has isExpanded: true
      setTimeout(() => {
        if (this.focusedDepartmentId) {
          const el = document.getElementById(`department${this.focusedDepartmentId}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 0);

      this.changeDetector.markForCheck();
      const paramChangeEndTime = performance.now();
      console.log(`paramChange took ${paramChangeEndTime - this.mainTimer} milliseconds`);
    });
  }

  mapEstimationToCost(){
    this.accordionList.forEach((department) => {
      department.items.forEach((item) => {
      const match = this.estimateItems.find((i) => i.itemLink === item.itemLink);
      item.estimatedCurrencyCode = match?.currencyCode ?? this.companyCurrencyCode;
      item.estimatedTotal = match?.total ?? 0;
      item.estimatedExchangeRate = match?.exchangeRate ?? 1;
      department.estimate = department.items.reduce((res, item) => res + item.estimatedTotal * item.estimatedExchangeRate, 0);
      department.items = [...department.items];
      this.changeDetector.markForCheck();
      });
    });
  }

  ngOnDestroy() {
    // ✅ FIXED: Use centralized cleanup method
    this.cleanupSubscriptions();
  }

  openModal(id: string, department?: ItemGroup) {
    if (department) {
      this.itemGroupCommentService
        .getItemGroupComments(this.projectCost._id, department._id)
        .subscribe((comments: Array<ItemGroupComment>) => {
          department.comments = comments;
          department.comments.forEach(
            (c) => (c.createdAt = new Date(c.createdAt))
          );
        });
    }
    this.modalService.open(id);
  }

  closeModal(id: string) {
    this.modalService.close(id);
    if (id === 'search-positions-m2') {
      document.getElementById('search-input').focus();
    }
  }

  waitForSortedPositions() {
    if (!this.sortedPositions || this.sortedPositions.length === 0) {
      setTimeout(() => this.waitForSortedPositions(), 50);
    }
  }

  scrollAfterContentLoads() {
    setTimeout(() => {
      if (!this.contentElements?.first?.nativeElement)
        this.scrollAfterContentLoads();
      else
        this.contentElements.first.nativeElement.scroll({
          top: this.contentElements.first.nativeElement.scrollHeight,
          behavior: 'smooth',
        });
    }, 100);
  }

  onRecalculate() {
    this.inHouseExpense = 0;
    this.projectExpense = 0;

    this.accordionList.forEach((c) => {
      c.actual = c.items.reduce(
        (res, i) =>
          res +
          (i.currencyCode !== this.companyCurrencyCode
            ? i.exchangeRate * i.total
            : i.total),
        0
      );
      this.inHouseExpense += c.items
        .filter((i) => i.costType?.toUpperCase() === 'IN-HOUSE')
        .reduce(
          (aggregator, pointer) =>
            aggregator +
            (pointer.currencyCode !== this.companyCurrencyCode
              ? pointer.exchangeRate * pointer.total
              : pointer.total),
          0
        );
      this.projectExpense += c.items
        .filter((i) => i.costType?.toUpperCase() !== 'IN-HOUSE')
        .reduce(
          (aggregator, pointer) =>
            aggregator +
            (pointer.currencyCode !== this.companyCurrencyCode
              ? pointer.exchangeRate * pointer.total
              : pointer.total),
          0
        );
    });

    this.projectCost.actualCost = this.accordionList.reduce(
      (res, c) => res + c.actual,
      0
    );
  }

  onAddProjectDepartment(department: string, isTeam: boolean) {
    this.addDepartment = false;
    this.addItemGroup = { department: department, name: '' };
    this.displayedPositions = this.sortedPositions.find(
      (p) => p.department === department
    ).positions;
  }

  togglePosition(position: { isSelected: boolean; name: string }) {
    position.isSelected = !position.isSelected;
    //keep order of selection
    if (position.isSelected) {
      this.positionsToAdd.push(position.name);
    } else {
      this.positionsToAdd = this.positionsToAdd.filter((p) => p !== position.name);
    }
    this.closeModal('search-positions-m2');
    this.searchResults = [];
    this.searchPosition = '';
  }

  onCancelPositions() {
    this.deselectPositions();
    this.addItemGroup = undefined;
    this.displayedPositions = [];
    this.searchPosition = '';
    this.positionsToAdd = [];
  }

  deselectPositions() {
    this.displayedPositions.forEach((p) => (p.isSelected = false));
  }

  onTypeSearch(event: any) {
    this.searchPosition = event.target.value;
    if (this.searchPosition.length > 1) {
      this.searchResults = this.allPositions
        .filter((p) =>
          p.name?.toUpperCase().includes(this.searchPosition?.toUpperCase())
        )
        .filter((p) => !this.positionsToAdd.find((pta) => pta == p.name))
        .map((p) => p.name);
      this.searchResults.unshift(this.translateService.instant('+ ADD NEW'));
      this.openModal('search-positions-m2', undefined);
      return;
    }
    this.closeModal('search-positions-m2');
  }

  onPositionSelected(position: string) {
    if (position === this.translateService.instant('+ ADD NEW')) {
      this.displayedPositions.push({
        name: this.searchPosition,
        isSelected: true,
        abbreviation: this.searchPosition,
      } as Position);
    } else {
      // REFACTOR !!!
      this.allPositions.find((p) => p.name === position).isSelected = true;
      this.displayedPositions.push(
        this.allPositions.find((p) => p.name === position)
      );
    }
    this.closeModal('search-positions-m2');
    this.searchResults = [];
    this.searchPosition = '';
    this.onTypeSearch({ target: { value: '' } });
  }

  onOkPositions() {
    const dept = this.projectCost.projectDepartments.find(
      (d) =>
        d.department === this.addItemGroup.department &&
        (d.name == this.addItemGroup.name ||
          (!d.name && !this.addItemGroup.name))
    );

    if (!dept) {
      let newDpt: CostAccordionCategory;
      this.groupDataService
        .createItemGroup(
          this.projectCost._id,
          this.departments.find((d) => d.name === this.addItemGroup.department)
            .isTeam,
          this.addItemGroup.department,
          this.addItemGroup.name
        )
        .pipe(
          switchMap((d) => {
            newDpt = {
              _id: d._id,
              name: this.addItemGroup.name,
              department: this.addItemGroup.department,
              items: [],
              positions: this.displayedPositions,
              actual: 0,
              estimate: 0,
              project: this.projectCost._id,
            };
            this.projectCost.projectDepartments.push(d as ItemGroup);
            this.accordionList.push(newDpt);
            return this.itemDataService.createMultipleCostItemsByGroup(
              this.projectCost._id,
              d._id,
              this.positionsToAdd
            );
          })
        )
        .subscribe((items: Array<CostItem>) => {
          (newDpt.items = items.map<DisplayItem>((i) => {
            return {
              ...i,
              estimatedTotal: 0,
              estimatedCurrencyCode: this.projectCurrencyCode,
            };
          })),
            this.onCancelPositions();
          this.scrollAfterContentLoads();
          this.deselectPositions();
          this.addItemGroup = undefined;
          this.positionsToAdd = [];
          this.changeDetector.markForCheck();
        });
      return;
    }
    this.itemDataService
      .createMultipleCostItemsByGroup(
        this.projectCost._id,
        dept._id,
        this.positionsToAdd
      )
      .subscribe((items: Array<CostItem>) => {
        // dept.items = dept.items.concat(items.map<DisplayItem>(i => {
        //   return {...i, estimatedTotal: 0}}));
        const accordionDept = this.accordionList.find(
          (c) => c._id === dept._id
        );
        accordionDept.items = accordionDept.items.concat(
          items.map<DisplayItem>((i) => {
            return {
              ...i,
              estimatedTotal: 0,
              estimatedCurrencyCode: this.projectCurrencyCode,
            };
          })
        );
        this.accordionList = [...this.accordionList];
        this.positionsToAdd = [];
        this.changeDetector.markForCheck();
      });
    this.deselectPositions();
    this.addItemGroup = undefined;
  }

  onDepartmentAction(data: { categoryId: string; selection: string }) {
    // console.log("data.selection ============ ", data.selection);
    switch (data.selection) {
      case 'delete': {
        const dept = this.accordionList.find((d) => d._id === data.categoryId);
        if (
          window.confirm(
            this.translateService.instant('windowConfirm.productionCost.deleteDepartment', {
              department: dept.department,
              name: dept.name
            })
          )
        ) {
          this.groupDataService
            .deleteItemGroup(this.projectCost._id, data.categoryId)
            .subscribe(() => {
              this.accordionList = this.accordionList.filter(
                (d) => data.categoryId != d._id
              );
              this.projectCost.projectDepartments =
                this.projectCost.projectDepartments.filter(
                  (d) => data.categoryId != d._id
                );
              this.changeDetector.markForCheck();
            });
        }
        return;
      }
      case 'rename': {
        this.renameDepartmentId = data.categoryId;
        return;
      }
      case 'renamed': {
        this.renameDepartmentId = '';
        return;
      }
      default:
        window.alert(this.translateService.instant('windowAlert.productionCost.underConstruction'));
        break;
    }
  }

  onMoveItem(moveData: { id: string; name: string; amount: number; department: string; description?: string; newDepartmentId: string }) {
    // Find the item to move
    const itemToMove = this.allItemsToDisplay.find(item => item._id === moveData.id);
    if (!itemToMove) {
      console.error('Item not found for moving');
      return;
    }

    // Find source and target categories
    const sourceCategory = this.accordionList.find(cat => cat._id === moveData.department);
    const targetCategory = this.accordionList.find(cat => cat._id === moveData.newDepartmentId);
    
    if (!sourceCategory || !targetCategory) {
      console.error('Source or target category not found');
      return;
    }
 
    // Call the service to update the backend
    this.itemDataService.moveCostItemToDepartment(
      moveData.id, 
      moveData.newDepartmentId, 
      this.pm.getCurrentProject()?._id
    ).subscribe({
      next: (response) => {
        // console.log('Cost item moved successfully:', response);
        
        // Update the item's itemGroup
        itemToMove.itemGroup = moveData.newDepartmentId;

        // Remove from source category
        sourceCategory.items = sourceCategory.items.filter(item => item._id !== moveData.id);
        
        // Add to target category
        targetCategory.items.push(itemToMove);

        // Recalculate totals for both categories
        this.recalculateCategoryTotals(sourceCategory);
        this.recalculateCategoryTotals(targetCategory);

        // Update the accordion list to trigger UI refresh
        this.accordionList = [...this.accordionList];
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        console.error('Error moving cost item:', error);
        // Optionally show an error message to the user
        // You could add a toast notification or error handling here
      }
    });
  }

  private recalculateCategoryTotals(category: CostAccordionCategory) {
    category.estimate = !this.noEstimateApproved
      ? category.items.reduce((res, i) => res + (i.estimatedTotal ?? 0), 0)
      : 0;
    category.actual = category.items.reduce((res, i) => res + (i.total ?? 0), 0) ?? 0;
  }

  onShowAddDepartmentScreen() {
    this.addDepartment = true;
  }

  onHideAddDepartmentScreen() {
    this.addDepartment = false;
  }

  resetList() {
    this.accordionList = [];
    this.projectCost.projectDepartments.forEach((d) => {
      const costItems = this.allItemsToDisplay.filter(
        (i) => i.itemGroup === d._id
      );
      this.accordionList.push({
        _id: d._id,
        project: d.project,
        department: d.department,
        name: d.name,
        items: costItems.sort((a, b) => a.orderNumber - b.orderNumber),
        comments: d.comments,
        hasComment: d.hasComment,
        disabled: false,
        open: false,
        orderNumber: d.orderNumber,
        estimate: !this.noEstimateApproved
          ? costItems.reduce((res, i) => res + (i.estimatedTotal ?? 0), 0)
          : 0,
        positions: d.positions,
        actual: d.items.reduce((res, i) => res + (i.total ?? 0), 0) ?? 0,
      });
    });

    const accordionItemsSorted = this.accordionList.sort(
      (a, b) => a.orderNumber - b.orderNumber
    );

    const reMappedAccordionItems = accordionItemsSorted.map(
      (cis: CostAccordionCategory, index: number) => {
        return {
          ...cis,
          orderNumber: index,
        };
      }
    );

    this.accordionList = [...reMappedAccordionItems];
  }

  onSearchItems(searchParameter: string) {
    if (!searchParameter?.trim() || searchParameter.length < 1) {
      this.resetList();
      return;
    }
    const parameters = String(searchParameter)
      .toLowerCase()
      .replaceAll(/\s+/g, ' ')
      .replaceAll(',', '')
      .split(' ');

    let searchResults = this.allItemsToDisplay;

    parameters.forEach((param) => {
      const paidByOptionsToSearch = this.allPaidByOptionsFlat
        ?.filter((o) => {
          return String(o.name).toLowerCase().includes(param);
        })
        .map((o) => {
          return o.id;
        });

      const assignToOptionsToSearch = this.assignToValues
        ?.filter((a) => {
          return String(a.name).toLowerCase().includes(param);
        })
        .map((a) => {
          return a.id;
        });

      searchResults = searchResults.filter((i) => {
        for (const entry of Object.entries(i)) {
          //TODO: sanitize objects before iterating for better performance
          //console.log(entry[0].toLowerCase())
          if (
            [
              'createdat',
              '_id',
              'id',
              'updatedat',
              'ordernumber',
              'project',
              'editedby',
              'compositeof',
              'color',
              'hasreminder',
              'hascomment',
              '__v',
              'itemgroup',
              'itemlink',
              'company',
              'deletedat',
            ].includes(String(entry[0]).toLowerCase())
          ) {
            continue;
          }
          if (
            String(entry[0]).toLowerCase().includes('date') &&
            this.datePipe
              .transform(new Date(entry[1]), 'dd.MM.yyyy')
              .includes(param)
          ) {
            return true;
          }
          if (
            String(entry[1] || '')
              .toLowerCase()
              .includes(param)
          ) {
            return true;
          }
        }
        if (
          paidByOptionsToSearch &&
          paidByOptionsToSearch.length > 0 &&
          paidByOptionsToSearch.includes(i.paidBy)
        ) {
          return true;
        }
        if (
          assignToOptionsToSearch &&
          assignToOptionsToSearch.length > 0 &&
          assignToOptionsToSearch.includes(i.assignedTo)
        ) {
          return true;
        }
      });
    });

    this.accordionList = [
      {
        _id: '',
        project: '',
        department: 'Search results',
        items: searchResults.sort((a, b) => a.orderNumber - b.orderNumber),
        hasComment: false,
        disabled: false,
        open: true,
        estimate: !this.noEstimateApproved
          ? searchResults.reduce(
              (res, i) =>
                res +
                (i.currencyCode !== this.companyCurrencyCode
                  ? i.exchangeRate * i.estimatedTotal
                  : i.estimatedTotal),
              0
            )
          : 0,
        positions: this.allPositions,
        actual:
          searchResults.reduce(
            (res, i) =>
              res +
              (i.currencyCode !== this.companyCurrencyCode
                ? i.exchangeRate * i.total
                : i.total),
            0
          ) ?? 0,
      },
    ];

    setTimeout(() => {
      this.accordionList[0].open = true;
    }, 5);
  }

  get companyBaseCurrency() {
    return Miscellanious.currencySymbolFromCode(
      'en-US',
      this.companyCurrencyCode
    );
  }

  onCreditCardCreated() {
    this.isRefreshing = true;
    // Refresh paid-by options for all accordion items
    this.productionCostService.refreshPaidByOptions().then(() => {
      // Trigger change detection to update the UI
      this.changeDetector.markForCheck();
      this.refreshTrigger = !this.refreshTrigger;
      this.isRefreshing = false;
      
      // Notify other components (like cashflow) that a credit card was created
      this.dataSyncService.notifyCreditCardCreated();
      
      // this.modalService.close('credit-card-create-modal');
    });
    
  }

  onBankAccountCreated() { 
    this.isRefreshing = true;
    // Refresh paid-by options for all accordion items
    this.productionCostService.refreshPaidByOptions().then(() => {
      // Trigger change detection to update the UI
      this.changeDetector.markForCheck();
      this.refreshTrigger = !this.refreshTrigger;
      this.isRefreshing = false;
      // this.modalService.close('bank-account-create-modal');
    });
    
  }

  onAddNewPaidByOption(event: { type: string }) {
    if (event.type === 'CREDIT_CARD') {
      this.modalService.open('credit-card-create-modal');
    } else if (event.type === 'BANK_TRANSFER') {
      this.modalService.open('bank-account-create-modal');
    }
  }

  calculateProfitability() {
    this.profitability = (this.projectCost?.estimatedCost ?? 0) -
      (this.inHouseExpense ?? 0) -
      (this.projectExpense ?? 0);
  }
}
